// React entry for @govard/ai-chat-kit (spec §2.3 — React bindings).
//
// Phase 1 scope:
//   - `useAgent` hook returning the shape described in spec §2.3.
//   - `<ChatPanel>` with four variants (`sidebar | bottom-sticky |
//     primary-pane | modal`) rendered as minimal markup so apps can style via
//     classNames. No Tailwind/CSS-in-JS is shipped; the class names are stable
//     hooks (`ack-chat-panel`, `ack-chat-panel--sidebar`, etc.).
//   - `send()` is a no-op because `agent.chat()` is a Phase 2 stub; we still
//     append the user message + a placeholder assistant message so UI flows
//     can be prototyped.
import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { newConversationId } from '../create-agent.js';
import type {
  Agent,
  Approval,
  ApprovalDecision,
  BaseScope,
  Message,
  ToolCall,
  ToolResult,
  Usage,
} from '../types.js';

export type ChatPanelVariant = 'sidebar' | 'bottom-sticky' | 'primary-pane' | 'modal';

export interface UseAgentOptions<TScope extends BaseScope> {
  scope: TScope;
  conversationId?: string;
}

export interface UseAgentReturn {
  messages: Message[];
  pendingApprovals: Approval[];
  isStreaming: boolean;
  usage: Usage | null;
  conversationId: string;
  send(userInput: string): Promise<void>;
  approve(approvalId: string, decision: ApprovalDecision): Promise<void>;
  reset(): void;
}

function makeMessage(role: Message['role'], content: string): Message {
  return {
    id: newConversationId(),
    role,
    content,
    createdAt: new Date(),
  };
}

/**
 * Phase 1 `useAgent`: wires local state only. When `agent.chat()` is
 * implemented in Phase 2 the `send` closure will forward to it and stream
 * messages back.
 */
export function useAgent<TScope extends BaseScope>(
  agent: Agent<TScope>,
  options: UseAgentOptions<TScope>,
): UseAgentReturn {
  const { scope } = options;

  const [conversationId, setConversationId] = useState<string>(
    () => options.conversationId ?? newConversationId(),
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<Approval[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null);

  const send = useCallback(
    async (userInput: string): Promise<void> => {
      if (!userInput.trim()) return;
      if (!scope.workspaceId || !scope.userId) {
        // Keep behaviour consistent with core: no LLM call without Scope.
        throw new Error('[ai-chat-kit/react] useAgent.send: scope.workspaceId/userId required');
      }
      setIsStreaming(true);
      try {
        const userMsg = makeMessage('user', userInput);
        const placeholder = makeMessage(
          'assistant',
          `[PHASE-2] agent[${agent.config.id}] would reply here — wire agent.chat() to enable.`,
        );
        setMessages((prev) => [...prev, userMsg, placeholder]);
      } finally {
        setIsStreaming(false);
      }
    },
    [agent.config.id, scope.userId, scope.workspaceId],
  );

  const approve = useCallback(
    async (approvalId: string, decision: ApprovalDecision): Promise<void> => {
      setPendingApprovals((prev) =>
        prev.map((a) =>
          a.id === approvalId
            ? {
                ...a,
                status: decision === 'approve' ? 'approved' : 'rejected',
                decidedAt: new Date(),
              }
            : a,
        ),
      );
    },
    [],
  );

  const reset = useCallback((): void => {
    setMessages([]);
    setPendingApprovals([]);
    setUsage(null);
    setConversationId(newConversationId());
  }, []);

  return useMemo<UseAgentReturn>(
    () => ({
      messages,
      pendingApprovals,
      isStreaming,
      usage,
      conversationId,
      send,
      approve,
      reset,
    }),
    [messages, pendingApprovals, isStreaming, usage, conversationId, send, approve, reset],
  );
}

export interface ChatPanelProps {
  agent: UseAgentReturn;
  variant?: ChatPanelVariant;
  placeholder?: string;
  submitLabel?: string;
  initialMessages?: Message[];
  onApprovalDecision?: (approvalId: string, decision: ApprovalDecision) => void;
  onConversationChange?: (id: string) => void;
  classNames?: {
    root?: string;
    messages?: string;
    message?: string;
    toolCard?: string;
    approvalCard?: string;
    input?: string;
  };
  renderMessageContent?: (message: Message) => ReactNode;
  renderToolCard?: (call: ToolCall, result?: ToolResult) => ReactNode;
  renderApprovalCard?: (approval: Approval) => ReactNode;
  slots?: { header?: ReactNode; footer?: ReactNode };
  style?: CSSProperties;
  testIds?: {
    root?: string;
    input?: string;
    sendButton?: string;
  };
}

const VARIANT_CLASS: Record<ChatPanelVariant, string> = {
  sidebar: 'ack-chat-panel--sidebar',
  'bottom-sticky': 'ack-chat-panel--bottom-sticky',
  'primary-pane': 'ack-chat-panel--primary-pane',
  modal: 'ack-chat-panel--modal',
};

/**
 * Minimal Phase 1 `<ChatPanel>`. Renders the messages list, pending approvals,
 * and an input row. No streaming indicator beyond `isStreaming`. Apps override
 * markup via `classNames` + `render*` slots.
 */
export function ChatPanel(props: ChatPanelProps): ReactNode {
  const {
    agent,
    variant = 'sidebar',
    placeholder = 'Ask the assistant…',
    submitLabel = 'Send',
    classNames,
    renderMessageContent,
    renderApprovalCard,
    slots,
    style,
    onApprovalDecision,
    testIds,
  } = props;

  const [draft, setDraft] = useState('');

  const rootClass = ['ack-chat-panel', VARIANT_CLASS[variant], classNames?.root]
    .filter(Boolean)
    .join(' ');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const value = draft.trim();
    if (!value) return;
    setDraft('');
    await agent.send(value);
  };

  const handleApprove = (id: string, decision: ApprovalDecision): void => {
    void agent.approve(id, decision);
    onApprovalDecision?.(id, decision);
  };

  return (
    <section className={rootClass} style={style} data-variant={variant} data-testid={testIds?.root}>
      {slots?.header}
      <ul className={['ack-chat-panel__messages', classNames?.messages].filter(Boolean).join(' ')}>
        {agent.messages.map((m) => (
          <li
            key={m.id}
            className={[
              'ack-chat-panel__message',
              `ack-chat-panel__message--${m.role}`,
              classNames?.message,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {renderMessageContent ? renderMessageContent(m) : <span className="ack-chat-panel__content">{m.content}</span>}
          </li>
        ))}
      </ul>
      {agent.pendingApprovals.length > 0 && (
        <ul className="ack-chat-panel__approvals">
          {agent.pendingApprovals.map((approval) =>
            renderApprovalCard ? (
              <li key={approval.id}>{renderApprovalCard(approval)}</li>
            ) : (
              <li
                key={approval.id}
                className={['ack-chat-panel__approval-card', classNames?.approvalCard]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span>
                  Approval needed: <code>{approval.toolName}</code>
                </span>
                <button type="button" onClick={(): void => handleApprove(approval.id, 'approve')}>
                  Approve
                </button>
                <button type="button" onClick={(): void => handleApprove(approval.id, 'reject')}>
                  Reject
                </button>
              </li>
            ),
          )}
        </ul>
      )}
      <form className="ack-chat-panel__input-row" onSubmit={handleSubmit}>
        <input
          className={['ack-chat-panel__input', classNames?.input].filter(Boolean).join(' ')}
          type="text"
          value={draft}
          onChange={(e): void => setDraft(e.target.value)}
          placeholder={placeholder}
          disabled={agent.isStreaming}
          data-testid={testIds?.input}
        />
        <button type="submit" disabled={agent.isStreaming || !draft.trim()} data-testid={testIds?.sendButton}>
          {submitLabel}
        </button>
      </form>
      {slots?.footer}
    </section>
  );
}
